// src/utils/accessFilters.js
import { getCurrentUser } from "./auth";
import { getStoredAccessPrefs } from "./accessPreferenceStore";

export const ALL_ACCESS_TOKEN = "ALL";
export const NULL_ACCESS_TOKEN = "__NULL__";

const DEFAULT_CUST_KEYS = [
  "cust_code",
  "custcode",
  "customer_code",
  "customercode",
  "customer",
  "cust",
  "customer_id",
  "customerid",
  "cust_id",
  "custid",
  "company_code",
  "companycode",
];

const DEFAULT_WHS_KEYS = [
  "whse_code",
  "whsecode",
  "warehouse_code",
  "warehousecode",
  "warehouse",
  "whs_code",
  "whscode",
  "whs",
  "whse",
  "wh_id",
  "warehouse_id",
];

const TOKEN_SPLIT_RE = /[,;|]/;
const KEY_NORMALIZE_RE = /[^a-z0-9]/g;

const normalizeKeyName = (key) =>
  typeof key === "string" ? key.toLowerCase().replace(KEY_NORMALIZE_RE, "") : "";

const uniquePush = (target, value) => {
  if (!target.includes(value)) target.push(value);
};

export const parseAccessTokens = (input) => {
  if (!input) return [];
  let tokens;
  if (Array.isArray(input)) {
    tokens = input;
  } else if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        tokens = parsed;
      }
    } catch {
      // ignore JSON parse errors, fall back to split
    }
    if (!tokens) {
      tokens = trimmed.split(TOKEN_SPLIT_RE);
    }
  } else {
    return [];
  }

  const seen = new Set();
  const result = [];
  tokens.forEach((value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return;
    const upper = raw.toUpperCase();
    const normalized = upper === NULL_ACCESS_TOKEN ? NULL_ACCESS_TOKEN : upper;
    if (seen.has(upper)) return;
    seen.add(upper);
    if (normalized === ALL_ACCESS_TOKEN) {
      result.length = 0;
      result.push(ALL_ACCESS_TOKEN);
      return;
    }
    result.push(normalized);
  });
  return result;
};

const extractTokensFromValue = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    const nested = value.flatMap(extractTokensFromValue);
    const deduped = [];
    nested.forEach((token) => uniquePush(deduped, token));
    return deduped;
  }
  if (typeof value === "object") {
    if (value && typeof value.value !== "undefined") {
      return extractTokensFromValue(value.value);
    }
    try {
      return extractTokensFromValue(JSON.stringify(value));
    } catch {
      return extractTokensFromValue(String(value));
    }
  }
  const str = String(value).trim();
  if (!str) return [NULL_ACCESS_TOKEN];
  const parts = str.split(TOKEN_SPLIT_RE).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) {
    return [str.toUpperCase()];
  }
  const tokens = [];
  parts.forEach((part) => uniquePush(tokens, part.toUpperCase()));
  return tokens;
};

const tokensMatch = (rowTokens, allowedTokens, allowBlank) => {
  const allowNullToken = allowedTokens.includes(NULL_ACCESS_TOKEN);
  if (!allowedTokens.length) return true;
  if (allowedTokens.includes(ALL_ACCESS_TOKEN)) return true;
  const nonEmptyTokens = (rowTokens || []).filter((token) => token && token !== NULL_ACCESS_TOKEN);
  if (nonEmptyTokens.length === 0) {
    if (allowNullToken) return true;
    return allowBlank;
  }
  if (!rowTokens.length) {
    if (allowNullToken) return true;
    return allowBlank;
  }
  if (rowTokens.includes(ALL_ACCESS_TOKEN)) return true;
  return rowTokens.some((token) => allowedTokens.includes(token));
};

const collectSourceKeys = (rows, columns) => {
  const collected = [];
  const seen = new Set();
  (Array.isArray(columns) ? columns : []).forEach((key) => {
    if (!key) return;
    if (seen.has(key)) return;
    seen.add(key);
    collected.push(key);
  });
  rows.slice(0, 20).forEach((row = {}) => {
    Object.keys(row || {}).forEach((key) => {
      if (!key) return;
      if (seen.has(key)) return;
      seen.add(key);
      collected.push(key);
    });
  });
  return collected;
};

const resolveKeys = (rows, columns, explicitKeys, candidates) => {
  const sources = collectSourceKeys(rows, columns);
  if (!sources.length) return [];
  const lookup = new Map();
  sources.forEach((key) => {
    const normalised = normalizeKeyName(key);
    if (normalised && !lookup.has(normalised)) {
      lookup.set(normalised, key);
    }
  });

  const resolved = [];
  const pushResolved = (key) => {
    if (!key) return;
    const normalised = normalizeKeyName(key);
    if (!normalised) return;
    const actual = lookup.get(normalised);
    if (actual) uniquePush(resolved, actual);
  };

  (explicitKeys || []).forEach((key) => pushResolved(key));
  if (resolved.length === 0) {
    (candidates || []).forEach((candidate) => pushResolved(candidate));
  }
  return resolved;
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const shouldRestrict = (tokens, hasOverride) => {
  if (!tokens.length) return false;
  if (hasOverride) {
    return !tokens.includes(ALL_ACCESS_TOKEN);
  }
  return !tokens.includes(ALL_ACCESS_TOKEN);
};

export const getUserAccessInfo = () => {
  const user = getCurrentUser();
  const custTokens = parseAccessTokens(user?.cust_access ?? user?.custAccess);
  const whsTokens = parseAccessTokens(user?.whs_access ?? user?.whsAccess);

  const overrides = getStoredAccessPrefs(user) || {};
  const hasCustOverride = hasOwn(overrides, "cust");
  const hasWhsOverride = hasOwn(overrides, "whs");
  const overrideCustTokens = hasCustOverride ? parseAccessTokens(overrides.cust) : [];
  const overrideWhsTokens = hasWhsOverride ? parseAccessTokens(overrides.whs) : [];

  const activeCustTokens = hasCustOverride ? overrideCustTokens : custTokens;
  const activeWhsTokens = hasWhsOverride ? overrideWhsTokens : whsTokens;
  const restrictCust = shouldRestrict(activeCustTokens, hasCustOverride);
  const restrictWhs = shouldRestrict(activeWhsTokens, hasWhsOverride);

  return {
    user,
    custTokens,
    whsTokens,
    activeCustTokens,
    activeWhsTokens,
    hasCustOverride,
    hasWhsOverride,
    restrictCust,
    restrictWhs,
  };
};

export const filterRowsByAccess = (rows = [], options = {}) => {
  const {
    activeCustTokens = [],
    activeWhsTokens = [],
    restrictCust,
    restrictWhs,
  } = getUserAccessInfo();
  const {
    columns = [],
    custKeys = [],
    whsKeys = [],
    allowBlank = false,
    disableCust = false,
    disableWhs = false,
  } = options;

  const resolvedCustKeys = disableCust ? [] : resolveKeys(rows, columns, custKeys, DEFAULT_CUST_KEYS);
  const resolvedWhsKeys = disableWhs ? [] : resolveKeys(rows, columns, whsKeys, DEFAULT_WHS_KEYS);

  const enforceCust =
    !disableCust && resolvedCustKeys.length > 0 && restrictCust && activeCustTokens.length > 0;
  const enforceWhs =
    !disableWhs && resolvedWhsKeys.length > 0 && restrictWhs && activeWhsTokens.length > 0;

  if (!enforceCust && !enforceWhs) {
    return rows.slice();
  }

  return rows.filter((row) => {
    if (!row || typeof row !== "object") return false;
    if (enforceCust) {
      const rowTokens = resolvedCustKeys.flatMap((key) => extractTokensFromValue(row[key]));
      if (!tokensMatch(rowTokens, activeCustTokens, allowBlank)) return false;
    }
    if (enforceWhs) {
      const rowTokens = resolvedWhsKeys.flatMap((key) => extractTokensFromValue(row[key]));
      if (!tokensMatch(rowTokens, activeWhsTokens, allowBlank)) return false;
    }
    return true;
  });
};
